/**
 * =============================================================================
 * Página: ReferralPayouts (Pagamentos do módulo Indicação)
 * Versão: 2.0.0
 *
 * Descrição:
 *   Gestão de solicitações de saque (payouts) do tenant. Padronizada com a
 *   mesma estrutura visual e arquitetural da página de Cobranças do módulo
 *   Financeiro (`FinancialCharges.tsx`):
 *
 *   - Header compacto com título + descrição + MonthNavigator + Refresh.
 *   - 4 KPI cards horizontais (Pendente, Processando, Pago, Falho/Cancelado).
 *   - Barra inline de filtros rápidos + botão "Mais" abrindo Sheet lateral
 *     com filtros avançados (datas).
 *   - Tabela padrão + PaginationFooter.
 *   - 100% server-side via RPC `get_referral_payouts_paginated`
 *     (KPIs + total_count + linhas em uma única chamada). NENHUM SELECT
 *     direto na tabela é feito a partir desta página.
 *   - Realtime centralizado: invalida a queryKey paginada em qualquer
 *     mudança em `affiliate_payouts`.
 *
 *   IMPORTANTE: a baixa financeira real (PIX/transferência) é executada
 *   FORA do sistema — esta tela apenas registra o resultado para fins de
 *   auditoria do módulo Indicação. NÃO existe integração com o módulo
 *   Financeiro neste fluxo.
 *
 * Release notes:
 *   v1.0.0 - Versão inicial (client-side, sem paginação).
 *   v2.0.0 - Padronização com Financeiro/Cobranças. Migração para RPC
 *            paginada `get_referral_payouts_paginated`. KPIs server-side,
 *            MonthNavigator, Sheet de filtros avançados, PaginationFooter,
 *            Realtime centralizado.
 * =============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Wallet,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  MoreHorizontal,
  PlayCircle,
  RefreshCw,
  CalendarIcon,
  Briefcase,
  User as UserIcon,
  StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, format as formatDateFns } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import {
  calculatePeriodDates,
  type PeriodPreset,
} from '@/hooks/useCrmOverviewFilters';
// Helpers de layout padronizado (paridade total com ReferralCommissions)
import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  KpiCard,
  ListPageFiltersBar,
  LIST_FILTERS_INPUT_CLASS,
  LIST_FILTERS_SELECT_TRIGGER_CLASS,
  ListPageContent,
  ListPageFooter,
} from '@/components/layout/list-page';
import { cn, parseDateLocal } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { createLogger } from '@/lib/logger';
import {
  useReferralPayoutsPaginated,
  type PayoutStatus,
  type PayoutMethod,
  type PaginatedPayout,
  type PayoutPaginatedFilters,
} from '@/hooks/useReferralPayoutsPaginated';
import { useReferralPayoutMutations } from '@/hooks/useReferralPayoutsAdmin';
import { ReferralAffiliateSelector } from '@/components/referral/ReferralAffiliateSelector';
import { useSearchParams } from 'react-router-dom';
import { PayoutAttachmentsManager } from '@/components/referral/PayoutAttachmentsManager';
import { PayoutsTableView } from '@/components/referral/PayoutsTableView';
import { PayoutDetailSheet } from '@/components/referral/PayoutDetailSheet';
// v1.3.0 — Modal de drill-down das comissões vinculadas a um pagamento.
// Substitui as antigas colunas Negócio/Cliente/Indicação na listagem.
import { PayoutCommissionsModal } from '@/components/referral/PayoutCommissionsModal';
// Modais premium acionados a partir do PayoutDetailSheet e do
// PayoutCommissionsModal (CustomerDetailsModalPremium é o modal padrão do
// sistema, idêntico ao usado em CrmCustomers, CrmDealsPipeline, etc.).
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';

const logger = createLogger('ReferralPayouts');

// ============================================================================
// CONSTANTES DE EXIBIÇÃO
// ============================================================================

/** Labels de status (PT-BR). */
const STATUS_LABEL: Record<PayoutStatus, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  paid: 'Pago',
  failed: 'Falhou',
  cancelled: 'Cancelado',
};

/**
 * Cores de badge de status — mesmo padrão semântico de `FinancialCharges`
 * (`bg-{color}-100 text-{color}-800 dark:bg-{color}-900/30 dark:text-{color}-400`).
 */
const STATUS_BADGE_COLORS: Record<PayoutStatus, string> = {
  pending:
    'bg-yellow-500 text-white border-yellow-500',
  processing:
    'bg-blue-500 text-white border-blue-500',
  paid:
    'bg-green-500 text-white border-green-500',
  failed:
    'bg-red-500 text-white border-red-500',
  cancelled:
    'bg-muted-foreground text-white border-muted-foreground',
};

/** Labels de método de pagamento (PT-BR). */
const METHOD_LABEL: Record<PayoutMethod, string> = {
  pix: 'PIX',
  bank_transfer: 'Transferência',
  manual: 'Manual',
  credits: 'Crédito',
};

/** Formata valor monetário em BRL. */
function formatBRL(value: number | null | undefined) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value ?? 0));
}

/** Formata data ISO em pt-BR (dd/mm/yyyy). */
function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function ReferralPayouts() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // =========================================================================
  // v1.1.0 — Modais premium acionados pelas colunas "Nº Negócio" e "Cliente"
  // =========================================================================
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);

  // v1.3.0 — Sheet lateral de detalhes de um pagamento (PayoutDetailSheet).
  const [selectedPayoutId, setSelectedPayoutId] = useState<string | null>(null);
  // v1.3.0 — Modal de drill-down das comissões vinculadas a um pagamento.
  // Aberto pela coluna "Comissões" (pill clicável) da PayoutsTableView.
  const [commissionsModalPayoutId, setCommissionsModalPayoutId] = useState<string | null>(null);

  // =========================================================================
  // Estados de filtro
  // =========================================================================
  const [statusFilter, setStatusFilter] = useState<PayoutStatus | 'all'>('all');
  const [affiliateFilter, setAffiliateFilter] = useState<string | 'all'>('all');
  const [methodFilter, setMethodFilter] = useState<PayoutMethod | 'all'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filtro por mês (header — afeta requested_at)
  const [monthFilter, setMonthFilter] = useState<Date | null>(new Date());
  // v2.1.0 (2026-07-24) — Preset do MonthNavigator.
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>('month');
  const [customRange, setCustomRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [dataSolicitacaoDe, setDataSolicitacaoDe] = useState<string | null>(
    () => formatDateFns(startOfMonth(new Date()), 'yyyy-MM-dd'),
  );
  const [dataSolicitacaoAte, setDataSolicitacaoAte] = useState<string | null>(
    () => formatDateFns(endOfMonth(new Date()), 'yyyy-MM-dd'),
  );

  // Filtros avançados (Sheet "Mais")
  const [dataPagamentoDe, setDataPagamentoDe] = useState<string | null>(null);
  const [dataPagamentoAte, setDataPagamentoAte] = useState<string | null>(null);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  // Paginação server-side
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Diálogos de ação (preservados da v1)
  const [payTarget, setPayTarget] = useState<string | null>(null);
  const [externalId, setExternalId] = useState('');
  // v2.1.0 — Data efetiva do pagamento (default = hoje) e observações livres
  // que ficam atreladas ao payout (também ficam visíveis ao afiliado).
  const [payPaidAt, setPayPaidAt] = useState<string>(() =>
    formatDateFns(new Date(), 'yyyy-MM-dd'),
  );
  const [payNotes, setPayNotes] = useState('');
  const [failTarget, setFailTarget] = useState<string | null>(null);
  const [failReason, setFailReason] = useState('');
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  // =========================================================================
  // Hooks de dados
  // =========================================================================
  // Filtro de afiliado agora usa busca server-side via ReferralAffiliateSelector
  // (Popover + Command com debounce), substituindo o antigo Select estático.
  const {
    approveMutation,
    markPaidMutation,
    markFailedMutation,
    cancelMutation,
  } = useReferralPayoutMutations();

  // Debounce da busca textual (300ms) — mesmo padrão de FinancialCharges
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Monta filtros para a RPC
  const filters: PayoutPaginatedFilters = useMemo(
    () => ({
      status: statusFilter !== 'all' ? statusFilter : null,
      affiliate_id: affiliateFilter !== 'all' ? affiliateFilter : null,
      payout_method: methodFilter !== 'all' ? methodFilter : null,
      search: debouncedSearch || null,
      data_solicitacao_de: dataSolicitacaoDe,
      data_solicitacao_ate: dataSolicitacaoAte,
      data_pagamento_de: dataPagamentoDe,
      data_pagamento_ate: dataPagamentoAte,
    }),
    [
      statusFilter,
      affiliateFilter,
      methodFilter,
      debouncedSearch,
      dataSolicitacaoDe,
      dataSolicitacaoAte,
      dataPagamentoDe,
      dataPagamentoAte,
    ],
  );

  const {
    payouts,
    totalCount,
    kpis,
    isLoading,
    isFetching,
    refetch,
  } = useReferralPayoutsPaginated(filters, {
    page: currentPage,
    pageSize,
  });

  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  // =========================================================================
  // Realtime centralizado — invalida cache em qualquer mudança em payouts
  // =========================================================================
  useEffect(() => {
    if (!currentTenant?.id) return;

    const channel = supabase
      .channel(`affiliate-payouts-page-rt-${currentTenant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affiliate_payouts',
          filter: `tenant_id=eq.${currentTenant.id}`,
        },
        (payload) => {
          logger.info('Realtime: payout alterado', {
            eventType: payload.eventType,
          });
          queryClient.invalidateQueries({
            queryKey: ['referral-payouts-paginated', currentTenant.id],
          });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentTenant?.id, queryClient]);

  // =========================================================================
  // Deep-link da tela Comissões: ?highlight=<payout_id>&action=pay
  //   - Destaca a linha do payout recém-criado.
  //   - Quando action=pay, abre automaticamente o diálogo "Marcar como pago"
  //     para o admin concluir em 1 clique.
  // =========================================================================
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightPayoutId = searchParams.get('highlight');
  const highlightAction = searchParams.get('action');

  useEffect(() => {
    if (!highlightPayoutId || highlightAction !== 'pay') return;
    if (isLoading) return;
    // Confirma que o payout faz parte da página atual antes de abrir o diálogo.
    const found = payouts.find((p) => p.id === highlightPayoutId);
    if (!found) return;
    setPayTarget(highlightPayoutId);
    // Limpa o `action` para não reabrir o diálogo a cada re-render,
    // mas mantém o `highlight` para o ring visual.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightPayoutId, highlightAction, isLoading, payouts]);

  // Reseta página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    affiliateFilter,
    methodFilter,
    dataSolicitacaoDe,
    dataSolicitacaoAte,
    dataPagamentoDe,
    dataPagamentoAte,
  ]);

  // =========================================================================
  // Handlers de UI
  // =========================================================================
  const handleRefresh = useCallback(() => {
    triggerRefresh(async () => {
      refetch();
    });
  }, [triggerRefresh, refetch]);

  const handleMonthChange = useCallback((newDate: Date) => {
    setMonthFilter(newDate);
    setActivePreset('month');
    setCustomRange(null);
    setDataSolicitacaoDe(formatDateFns(startOfMonth(newDate), 'yyyy-MM-dd'));
    setDataSolicitacaoAte(formatDateFns(endOfMonth(newDate), 'yyyy-MM-dd'));
  }, []);

  const handleMonthClear = useCallback(() => {
    setMonthFilter(null);
    setActivePreset(null);
    setCustomRange(null);
    setDataSolicitacaoDe(null);
    setDataSolicitacaoAte(null);
  }, []);

  /** v2.1.0 — Aplica preset ao filtro de data de solicitação. */
  const handlePresetChange = useCallback((preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'month') {
      const now = new Date();
      setMonthFilter(now);
      setCustomRange(null);
      setDataSolicitacaoDe(formatDateFns(startOfMonth(now), 'yyyy-MM-dd'));
      setDataSolicitacaoAte(formatDateFns(endOfMonth(now), 'yyyy-MM-dd'));
      return;
    }
    setCustomRange(null);
    const { startDate, endDate } = calculatePeriodDates(preset);
    setDataSolicitacaoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataSolicitacaoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  const handleCustomPeriodChange = useCallback((startDate: Date, endDate: Date) => {
    setActivePreset('custom');
    setCustomRange({ startDate, endDate });
    setDataSolicitacaoDe(formatDateFns(startDate, 'yyyy-MM-dd'));
    setDataSolicitacaoAte(formatDateFns(endDate, 'yyyy-MM-dd'));
  }, []);

  /** Contador de filtros avançados ativos (badge do botão "Mais"). */
  const advancedFilterCount = useMemo(() => {
    let count = 0;
    if (dataPagamentoDe || dataPagamentoAte) count++;
    return count;
  }, [dataPagamentoDe, dataPagamentoAte]);

  const hasActiveFilters =
    !!search ||
    statusFilter !== 'all' ||
    affiliateFilter !== 'all' ||
    methodFilter !== 'all' ||
    advancedFilterCount > 0;

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setAffiliateFilter('all');
    setMethodFilter('all');
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
  }, []);

  const clearAdvancedFilters = useCallback(() => {
    setDataPagamentoDe(null);
    setDataPagamentoAte(null);
  }, []);

  // =========================================================================
  // Mutations — diálogos (preservados da v1)
  // =========================================================================
  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      toast.success('Solicitação marcada como em processamento.');
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao processar solicitação.');
    }
  };

  const confirmPay = async () => {
    if (!payTarget) return;
    try {
      // Converte a data informada (yyyy-MM-dd) para ISO no início do dia local.
      // A RPC `confirm_payout` aceita timestamptz e valida que a data não seja futura.
      const paidAtIso = payPaidAt
        ? new Date(`${payPaidAt}T12:00:00`).toISOString()
        : null;

      await markPaidMutation.mutateAsync({
        id: payTarget,
        externalTransactionId: externalId.trim() || null,
        notes: payNotes.trim() || null,
        paidAt: paidAtIso,
      });
      toast.success('Pagamento registrado e comissões atualizadas.');
      setPayTarget(null);
      setExternalId('');
      setPayNotes('');
      setPayPaidAt(formatDateFns(new Date(), 'yyyy-MM-dd'));
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao registrar pagamento.');
    }
  };

  const confirmFail = async () => {
    if (!failTarget) return;
    try {
      await markFailedMutation.mutateAsync({
        id: failTarget,
        reason: failReason.trim() || null,
      });
      toast.success('Solicitação marcada como falha. Comissões liberadas.');
      setFailTarget(null);
      setFailReason('');
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao registrar a falha.');
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelMutation.mutateAsync({
        id: cancelTarget,
        reason: cancelReason.trim() || null,
      });
      toast.success('Solicitação cancelada. Comissões liberadas.');
      setCancelTarget(null);
      setCancelReason('');
    } catch (e: any) {
      toast.error(e?.message || 'Falha ao cancelar solicitação.');
    }
  };

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <ListPageLayout>
      {/* ================================================================ */}
      {/* Header padronizado (helper) — centraliza MonthNavigator           */}
      {/* paridade visual com a página de Comissões                        */}
      {/* ================================================================ */}
      <ListPageHeader
        title="Pagamentos"
        description="Solicitações de saque dos afiliados."
        center={
          monthFilter || (activePreset && activePreset !== 'month') ? (
            <MonthNavigator
              currentMonth={monthFilter ?? new Date()}
              onChange={handleMonthChange}
              onClear={handleMonthClear}
              activePreset={activePreset}
              customRange={customRange}
              onPresetChange={handlePresetChange}
              onCustomChange={handleCustomPeriodChange}
            />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMonthChange(new Date())}
              className="text-xs text-muted-foreground"
            >
              <CalendarIcon className="h-3.5 w-3.5 mr-1" />
              Filtrar por mês
            </Button>
          )
        }
        actions={
          // Padrão TaskList: indicadores acima, botões de ação abaixo
          <div className="flex flex-col items-end gap-1.5">
            <ReferralUsageInline className="hidden md:inline-flex" />
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Atualizar dados"
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')}
                />
              </Button>
            </div>
          </div>
        }
      />

      {/* ================================================================ */}
      {/* KPIs padronizados (helper) — paridade total com Comissões         */}
      {/* ================================================================ */}
      {!isLoading && (
        <ListPageKpis>
          <KpiCard color="yellow" icon={Clock}        label="Pendentes"             value={formatBRL(kpis.totalPendente)} />
          <KpiCard color="blue"   icon={PlayCircle}   label="Processando"           value={formatBRL(kpis.totalProcessando)} />
          <KpiCard color="green"  icon={CheckCircle2} label="Pagos"                 value={formatBRL(kpis.totalPago)} />
          <KpiCard color="red"    icon={XCircle}      label="Falhos / Cancelados"   value={formatBRL(kpis.totalFalhoCancelado)} />
        </ListPageKpis>
      )}

      {/* ================================================================ */}
      {/* Barra de filtros rápidos (helper)                                 */}
      {/* ================================================================ */}
      <ListPageFiltersBar
        onMoreClick={() => setIsMoreFiltersOpen(true)}
        moreCount={advancedFilterCount}
        isFetching={isFetching && !isLoading}
        rightSlot={
          hasActiveFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-6 px-2 text-[10px] sm:h-8 sm:px-2.5 sm:text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar
            </Button>
          ) : undefined
        }
      >
        {/* Busca livre */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por afiliado, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn('pl-8', LIST_FILTERS_INPUT_CLASS)}
          />
        </div>

        {/* Status */}
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as PayoutStatus | 'all')}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {/* Mantém o nome do filtro visível no trigger (padrão TaskList) */}
            <SelectItem value="all">Status</SelectItem>
            {Object.entries(STATUS_LABEL).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Afiliado — busca server-side (nome, e-mail, telefone, CPF/CNPJ) */}
        <div className="w-full sm:w-[220px]">
          <ReferralAffiliateSelector
            value={affiliateFilter === 'all' ? null : affiliateFilter}
            onChange={(id) => setAffiliateFilter(id ?? 'all')}
            placeholder="Filtrar por afiliado..."
          />
        </div>

        {/* Método de pagamento */}
        <Select
          value={methodFilter}
          onValueChange={(v) => setMethodFilter(v as PayoutMethod | 'all')}
        >
          <SelectTrigger className={cn(LIST_FILTERS_SELECT_TRIGGER_CLASS, 'sm:w-[140px]')}>
            <SelectValue placeholder="Método" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Método</SelectItem>
            {Object.entries(METHOD_LABEL).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      {/* Sheet de filtros avançados */}
      <Sheet open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
        <SheetContent side="right" className="w-80 p-0 flex flex-col">
          <SheetHeader className="px-4 pt-4 pb-3 border-b">
            <SheetTitle className="flex items-center justify-between text-sm font-semibold">
              <span>Filtros Avançados</span>
              {advancedFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAdvancedFilters}
                  className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Limpar
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-4 space-y-4">
              <Separator />

              {/* Filtro Data de Pagamento */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Data de Pagamento
                </Label>
                <div className="flex gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          'flex-1 h-8 text-xs justify-start',
                          !dataPagamentoDe && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        {dataPagamentoDe
                          ? format(parseDateLocal(dataPagamentoDe), 'dd/MM/yy')
                          : 'De'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          dataPagamentoDe
                            ? parseDateLocal(dataPagamentoDe)
                            : undefined
                        }
                        onSelect={(d) =>
                          setDataPagamentoDe(d ? format(d, 'yyyy-MM-dd') : null)
                        }
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          'flex-1 h-8 text-xs justify-start',
                          !dataPagamentoAte && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="h-3 w-3 mr-1" />
                        {dataPagamentoAte
                          ? format(parseDateLocal(dataPagamentoAte), 'dd/MM/yy')
                          : 'Até'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          dataPagamentoAte
                            ? parseDateLocal(dataPagamentoAte)
                            : undefined
                        }
                        onSelect={(d) =>
                          setDataPagamentoAte(d ? format(d, 'yyyy-MM-dd') : null)
                        }
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* ================================================================ */}
      {/* Tabela (helper) — full-bleed para paridade 1:1 com Comissões      */}
      {/* Usa o componente padronizado PayoutsTableView (StatusBar lateral, */}
      {/* table-fixed com colgroup e larguras idênticas à de Comissões).    */}
      {/* ================================================================ */}
      <ListPageContent fullBleed>
        <PayoutsTableView
          payouts={payouts}
          isLoading={isLoading}
          highlightId={highlightPayoutId}
          onOpenDetails={(payoutId) => setSelectedPayoutId(payoutId)}
          // v1.3.0 — pill clicável "N comissões" abre o modal de drill-down.
          onOpenCommissions={(p) => setCommissionsModalPayoutId(p.id)}
          onAction={(p, action) => {
            // Mapeia a ação selecionada no menu da linha para o handler/diálogo
            // existente — preserva 100% da lógica original.
            switch (action) {
              case 'approve':
                handleApprove(p.id);
                break;
              case 'mark_paid':
                setPayTarget(p.id);
                break;
              case 'mark_failed':
                setFailTarget(p.id);
                break;
              case 'cancel':
                setCancelTarget(p.id);
                break;
            }
          }}
        />
      </ListPageContent>

      {/* ================================================================ */}
      {/* Footer de paginação server-side (helper)                          */}
      {/* ================================================================ */}
      {!isLoading && payouts.length > 0 && (
        <ListPageFooter>
          <PaginationFooter
            totalItems={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        </ListPageFooter>
      )}

      {/* ============================================================= */}
      {/* Diálogos de ação (preservados da v1)                           */}
      {/* ============================================================= */}

      {/* Diálogo: Marcar como pago */}
      <Dialog
        open={!!payTarget}
        onOpenChange={(open) => {
          if (!open) {
            setPayTarget(null);
            setExternalId('');
            setPayNotes('');
            setPayPaidAt(formatDateFns(new Date(), 'yyyy-MM-dd'));
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar pagamento</DialogTitle>
            <DialogDescription>
              Confirma que o saque foi pago externamente. As comissões vinculadas
              também serão marcadas como pagas. Nenhum lançamento é feito no
              módulo Financeiro.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Linha 1: Data do pagamento + ID transação externa (paridade visual) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Data do pagamento (default = hoje) */}
              <div className="space-y-2">
                <Label htmlFor="paid-at">Data do pagamento</Label>
                <Input
                  id="paid-at"
                  type="date"
                  value={payPaidAt}
                  onChange={(e) => setPayPaidAt(e.target.value)}
                  // Bloqueia datas futuras (mesmo limite da RPC `confirm_payout`)
                  max={formatDateFns(new Date(), 'yyyy-MM-dd')}
                />
              </div>

              {/* ID transação externa */}
              <div className="space-y-2">
                <Label htmlFor="external-id">ID da transação (opcional)</Label>
                <Input
                  id="external-id"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  placeholder="ID PIX, comprovante..."
                  maxLength={120}
                />
              </div>
            </div>

            {/* Observações (visíveis ao afiliado) */}
            <div className="space-y-2">
              <Label htmlFor="pay-notes">Observações (opcional)</Label>
              <Textarea
                id="pay-notes"
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Ex.: pago via PIX banco X em 29/04, valor convertido..."
                rows={3}
                maxLength={500}
              />
              <p className="text-[11px] text-muted-foreground">
                Essas observações ficam visíveis para o afiliado no extrato.
              </p>
            </div>

            {/* Comprovantes (anexos múltiplos) — visíveis ao afiliado */}
            <PayoutAttachmentsManager
              payoutId={payTarget}
              title="Comprovantes de pagamento"
              description="Anexe imagens ou PDFs do comprovante. Esses arquivos ficarão disponíveis para o afiliado consultar no painel dele."
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>
              Voltar
            </Button>
            <Button onClick={confirmPay}>Confirmar pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Marcar como falho */}
      <Dialog
        open={!!failTarget}
        onOpenChange={(open) => {
          if (!open) {
            setFailTarget(null);
            setFailReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar como falho</DialogTitle>
            <DialogDescription>
              As comissões vinculadas serão liberadas para uma nova solicitação
              de saque pelo afiliado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="fail-reason">Motivo da falha</Label>
            <Textarea
              id="fail-reason"
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              placeholder="Ex.: chave PIX inválida, conta inexistente..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailTarget(null)}>
              Voltar
            </Button>
            <Button variant="destructive" onClick={confirmFail}>
              Marcar como falho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Cancelar */}
      <Dialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTarget(null);
            setCancelReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar solicitação</DialogTitle>
            <DialogDescription>
              As comissões vinculadas serão liberadas. O afiliado poderá
              solicitar um novo saque a qualquer momento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Motivo (opcional)</Label>
            <Textarea
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ex.: solicitação duplicada, dados incorretos..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              Voltar
            </Button>
            <Button variant="destructive" onClick={confirmCancel}>
              Confirmar cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ================================================================ */}
      {/* v1.2.0 — Modais premium das colunas clicáveis                     */}
      {/*   • DealDetailsModalPremium      → coluna "Nº Negócio"            */}
      {/*   • CustomerDetailsModalPremium  → coluna "Cliente" (modal padrão */}
      {/*                                    do sistema, mesmo do CRM)     */}
      {/* ================================================================ */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={(open) => {
          setIsDealModalOpen(open);
          if (!open) setSelectedDealId(null);
        }}
      />
      <CustomerDetailsModalPremium
        customerId={selectedCustomerId}
        open={isCustomerModalOpen}
        onOpenChange={(open) => {
          setIsCustomerModalOpen(open);
          if (!open) setSelectedCustomerId(null);
        }}
      />

      {/* ================================================================ */}
      {/* v1.3.0 — Sheet lateral de detalhes do pagamento                   */}
      {/* Reaproveita os mesmos handlers dos diálogos de ação existentes.   */}
      {/* ================================================================ */}
      <PayoutDetailSheet
        payoutId={selectedPayoutId}
        onClose={() => setSelectedPayoutId(null)}
        onAction={(payoutId, action) => {
          switch (action) {
            case 'approve':
              handleApprove(payoutId);
              break;
            case 'mark_paid':
              setPayTarget(payoutId);
              break;
            case 'mark_failed':
              setFailTarget(payoutId);
              break;
            case 'cancel':
              setCancelTarget(payoutId);
              break;
          }
        }}
        onOpenDeal={(dealId) => {
          setSelectedDealId(dealId);
          setIsDealModalOpen(true);
        }}
        onOpenCustomer={(customerId) => {
          setSelectedCustomerId(customerId);
          setIsCustomerModalOpen(true);
        }}
      />

      {/* ================================================================ */}
      {/* v1.3.0 — Modal de drill-down das comissões de um pagamento.      */}
      {/* Aberto pela coluna "Comissões" (pill) da PayoutsTableView e       */}
      {/* reusa os mesmos modais premium de Negócio/Cliente da página.     */}
      {/* ================================================================ */}
      <PayoutCommissionsModal
        payoutId={commissionsModalPayoutId}
        open={!!commissionsModalPayoutId}
        onClose={() => setCommissionsModalPayoutId(null)}
        onOpenDeal={(dealId) => {
          setSelectedDealId(dealId);
          setIsDealModalOpen(true);
        }}
        onOpenCustomer={(customerId) => {
          setSelectedCustomerId(customerId);
          setIsCustomerModalOpen(true);
        }}
      />
    </ListPageLayout>
  );
}

